/**
 * 群馬県の料理12品。レベルが上がるほど大きく、器つきの汁物・鍋物になる。
 *
 * 選定のときに迷った点を残しておく。
 * - 「ゆず巻き」は栃木にも同じ名前の品があるので、栃木に置いて群馬からは外した。
 * - 「炭酸まんじゅう」は栃木の「小麦まんじゅう」と見た目がほぼ同じなので外した。
 * - 「鮎塩焼き」は岐阜の「鮎の塩焼」と名前が重なるが、そのまま残すと決めた（2026-09-23）。
 *   送り仮名の違いで重複チェックの網から漏れていたもの。鮎は群馬の県魚で農水省に出典があり、
 *   絵も別物になっている。群馬は串を波のようにジグザグに打つ踊り串、岐阜はまっすぐ刺した形。
 *   あとから「重複だから」と片方を消さないこと。
 * - 「じりやき」は「ぎゅうてん」と同じ粉ものの焼き物になるので、桐生の「ぎゅうてん」に絞った。
 * - こんにゃくは山形の玉こんにゃく（球）、滋賀の赤こんにゃく（赤い煮物）とすでに出ている。
 *   群馬は三角に切って厚さ8ミリ、串に刺して味噌だれをのせる形なので、three とも重ならない。
 * - 「おっきりこみ」は無塩で下ゆでをしない約1センチ幅の幅広麺で、うどんとは別物。
 * - すき焼きは牛肉が他県に多く出ているので入れていない。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：まゆ玉・花いんげんの煮豆・やきもち・上州きんぴら・ねぎぬた・あまねじ・こしね汁（店で食べられる品に差し替え）
 * - 太田焼きそば：「日本三大焼きそば」は書かない。太田市公式は「全国に3つしかない『焼そばの街』秋田県横手市・静岡県富士宮市・太田市が、三国同麺を結び」と書くが「三大」の語はない。群馬県観光公式サイトの県民ライター記事（https://gunma-kanko.jp/features/252）には「日本三大焼きそばのひとつに数えられています」とあるが、県民ライターの寄稿記事なので採らなかった
 * - 太田焼きそば：「具はキャベツだけ」「太い蒸し麺」は書かない。市・観光物産協会のページには具の記述がなく、協会は太麺・細麺など店ごとに違うと書いている
 * - 太田焼きそば：上州太田焼そばのれん会の公式サイトは http のみ（http://www.chobee.jp/ootayakisoba/）なので出典にしない。のれん会の発会（平成14年10月28日）は市のページにあるが、tip の字数に収めるため今回は入れていない
 * - 上州の鳥めし：特定の老舗店名は書かない
 * - 上州の鳥めし：発祥の店・年は公式資料に記載がないため書かない
 * - 水沢うどん：水澤寺（水澤観世音）の参拝客に出したのが始まり、などの由来は今回開いた公式資料に無いため書かない
 * - 水沢うどん：「日本三大うどん」は農水省が『と言われている』と書いている範囲で、伝聞形にとどめた
 * - 桐生ソースかつ丼：農水省ページに『元祖はとある鰻屋と言われ、鰻のタレにウスターソースをブレンドした』とあるが、発祥の店にかかわる話で店も特定できないため書かない
 * - 桐生ソースかつ丼：特定の店名は書かない
 * - ひもかわうどん：発祥・名前の由来は公式資料に無いため書かない
 * - 峠の釜めし：「宮内庁御用達」「日本で最古の駅弁屋」は書かない。安中市「釜めし」ページ（https://www.city.annaka.lg.jp/kanko_spot/kamameshi.html）にあるが、製造する一社についての称号で、店の宣伝になるため
 * - 峠の釜めし：「元祖」は書かない。群馬県観光公式サイトの記事に「『おぎのや』さんが元祖」とあるが、特定の店についての主張なので
 * - 峠の釜めし：店名・住所・値段は書かない
 * - 上州牛：上州牛の定義（品種・等級・生産者）は今回開いた資料に無いため書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { gunmaSceneryTour } from './gunmaScenes';
import { GUNMA_FOOD_HULLS } from './gunmaFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'konnyaku-oden',
    level: 1,
    name: 'こんにゃく味噌おでん',
    shortName: 'こんにゃく味噌おでん',
    image: '/assets/foods/gunma/01_konnyaku_oden.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: 'こんにゃくを三角形かつ8ミリくらいの厚さに切って湯で4〜5分ゆがき、熱いうちに味噌と砂糖と酒とみりんを混ぜた味噌だれをのせる。串に刺して食べる。冬に食べることが多い。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_23_gunma.html',
    icon: { label: 'こんにゃく味噌おでん', shape: 'dumpling', baseColor: '#6b6455', accentColor: '#8a4a20' },
    reward: {
      unlockScore: 200,
      area: '県内全域',
      headline: 'こんにゃくは全国一位',
      tip: '群馬のこんにゃく芋の収穫量は全国第1位。令和6年産では栽培面積2,690ヘクタールで全国の92.4%、収穫量49,700トンで全国の96.7%を占める。',
      links: searchLinks('下仁田町', 'こんにゃく味噌おでん'),
    },
  },
  {
    id: 'yaki-manju',
    level: 2,
    name: '焼きまんじゅう',
    shortName: '焼きまんじゅう',
    image: '/assets/foods/gunma/02_yaki_manju.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: 'もち米をかゆ状に煮て米糀を混ぜ発酵させた「もと種」を小麦粉と混ぜ、約20グラムに分けてせいろで蒸し、竹串に刺す。水あめ、ざらめ、赤味噌、カラメルを煮詰めた味噌だれを塗って焼き上げる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_26_gunma.html',
    icon: { label: '焼きまんじゅう', shape: 'dumpling', baseColor: '#8a4a1a', accentColor: '#e8dcc0' },
    reward: {
      unlockScore: 500,
      area: '前橋市',
      headline: '串に刺すという発想',
      tip: '江戸時代の末に前橋の原嶋類蔵が作った「味噌づけまんじゅう」が発祥とされる。当時はどぶろくを発酵剤にしたことと、竹串に刺すという発想が珍しかった。沼田や利根郡では餡の入った甘いものも食べられている。',
      links: searchLinks('前橋市', '焼きまんじゅう'),
    },
  },
  {
    id: 'ota-yakisoba',
    level: 3,
    name: '太田焼きそば',
    shortName: '太田焼きそば',
    image: '/assets/foods/gunma/03_ota_yakisoba.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '太田市は焼きそばを出す店が多い工業都市。戦後、工場へ東北から多くの季節労働者が来て、秋田県横手市などから焼きそばを持ち込んだと考えられている。安くて量があり、汁がないのでのびないことから、工場で働く人たちに受け入れられた。',
    source:
      'https://www.city.ota.gunma.jp/page/1570.html',
    icon: { label: '焼きそば', shape: 'noodle-bowl', baseColor: '#6b3f1f', accentColor: '#8fb04a' },
    reward: {
      unlockScore: 1000,
      area: '太田市',
      headline: '工場のまちに根づいた焼きそば',
      tip: '麺の太さも味付けも店ごとに違い、太麺も細麺も、甘めもしょっぱめもある。昭和20〜30年代には大光院の参道に多くの焼そば屋が並んでいた。',
      links: searchLinks('太田市', '太田焼きそば'),
      official: [{ label: '太田市「焼そばのまち」', url: 'https://www.city.ota.gunma.jp/page/1570.html' }, { label: '太田市観光物産協会「上州太田焼そば」', url: 'https://www.ota-kanko.jp/%E4%B8%8A%E5%B7%9E%E5%A4%AA%E7%94%B0%E7%84%BC%E3%81%9D%E3%81%B0/' }],
    },
  },
  {
    id: 'gyuten',
    level: 4,
    name: 'ぎゅうてん',
    shortName: 'ぎゅうてん',
    image: '/assets/foods/gunma/04_gyuten.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '薄力粉を水で溶いて長ねぎと干しエビを加え、フライパンでヘラで「ギュー」と押しつけながら両面を焼く。小判型に焼いて切り、ソースと青のりをかける。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_27_gunma.html',
    icon: { label: 'ぎゅうてん', shape: 'mochi', baseColor: '#b07a3a', accentColor: '#2f5c2a' },
    reward: {
      unlockScore: 1800,
      area: '桐生市',
      headline: 'ギューと押して焼く',
      tip: '桐生では絹織物の生産がさかんで、家庭を守る女性も働きに出ていた。忙しいなかでもさっと作れるので、子どものおやつにも食事の副菜にもなった。',
      links: searchLinks('桐生市', 'ぎゅうてん'),
    },
  },
  {
    id: 'ayu-shioyaki',
    level: 5,
    name: '鮎塩焼き',
    shortName: '鮎塩焼き',
    image: '/assets/foods/gunma/05_ayu_shioyaki.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '鮎に塩を振り、波のようにジグザグさせながら串に刺して、炭火で1時間半かけて焼く。鮎は群馬の県魚に指定されている。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_17_gunma.html',
    icon: { label: '鮎塩焼き', shape: 'fish', baseColor: '#b0a68c', accentColor: '#e8e2d0' },
    reward: {
      unlockScore: 3000,
      area: '上野村',
      headline: '串を波のように打つ',
      tip: '上野村には利根川水系の神流川が流れ、神流川の鮎は香り高く身が引き締まった逸品とされる。毎年6月中旬ごろに鮎釣りが解禁される。',
      links: searchLinks('上野村', '鮎の塩焼き'),
    },
  },
  {
    id: 'joshu-torimeshi',
    level: 6,
    name: '上州の鳥めし',
    shortName: '上州の鳥めし',
    image: '/assets/foods/gunma/06_joshu_torimeshi.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '焼いてたれに漬けた薄切りの鶏肉をご飯の上にのせ、丼のようにして一緒に食べる。家庭でも作られ、県内の飲食店でも出される。老舗の店では秘伝のたれを使い、古くからのファンが多い。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_18_gunma.html',
    icon: { label: '鳥めし', shape: 'rice-bowl', baseColor: '#7a4a22', accentColor: '#f3ecd9' },
    reward: {
      unlockScore: 4500,
      area: '群馬県全域',
      headline: 'たれを絡めた鶏をご飯にのせる',
      tip: '群馬には桑の葉と梅酢を餌に育てる「上州地鶏」があり、鶏へのこだわりから鶏めしが長く親しまれてきた。行楽の弁当やテイクアウトにもされる。',
      links: searchLinks('群馬県', '鳥めし'),
      official: [{ label: '農林水産省「うちの郷土料理」鶏めし', url: 'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_18_gunma.html' }],
    },
  },
  {
    id: 'okkirikomi',
    level: 7,
    name: 'おっきりこみ',
    shortName: 'おっきりこみ',
    image: '/assets/foods/gunma/07_okkirikomi.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '麺の生地を麺棒などに巻いて包丁で切り込みを入れることから名がついた。約1センチ幅の無塩の幅広麺を、ゆでずにそのまま野菜やきのこと煮込む。2014年に群馬県記録作成等の措置を講ずべき無形の民俗文化財に採択された。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_1_gunma.html',
    icon: { label: 'おっきりこみ', shape: 'pot', baseColor: '#e8ddc0', accentColor: '#8a5a2a' },
    reward: {
      unlockScore: 6500,
      area: '県内全域',
      headline: '塩を入れず、ゆでずに煮る',
      tip: '石臼が庶民に広まった江戸時代の中ごろから日常の主食になった。無塩で下ゆでなしの麺を日常的に食べる地域は珍しく、群馬のほかは埼玉、山梨、長野などに限られる。',
      links: searchLinks('高崎市', 'おっきりこみ'),
    },
  },
  {
    id: 'mizusawa-udon',
    level: 8,
    name: '水沢うどん',
    shortName: '水沢うどん',
    image: '/assets/foods/gunma/08_mizusawa_udon.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '渋川市伊香保町の水沢地区の飲食店で出すうどん。つやとコシがあり、もちっとした食感をもつ。そのつやとコシを楽しむため、基本的にざるで出され、シンプルに食べることが多い。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_11_gunma.html',
    icon: { label: '水沢うどん', shape: 'noodle-bowl', baseColor: '#f4f1e8', accentColor: '#b8935a' },
    reward: {
      unlockScore: 9000,
      area: '渋川市伊香保町水沢',
      headline: 'つやとコシのざるうどん',
      tip: '秋田の稲庭うどん、香川の讃岐うどんと並んで日本三大うどんと言われている。水沢うどん商標登録店組合が「伊香保水沢うどんマップ」を作っている。',
      links: searchLinks('渋川市伊香保町水沢', '水沢うどん'),
      official: [{ label: '農林水産省「うちの郷土料理」群馬のうどん', url: 'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_11_gunma.html' }],
    },
  },
  {
    id: 'kiryu-sauce-katsudon',
    level: 9,
    name: '桐生ソースかつ丼',
    shortName: 'ソースかつ丼',
    image: '/assets/foods/gunma/09_kiryu_sauce_katsudon.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '揚げたての豚ヒレ肉のカツをソースにくぐらせ、丼に盛ったご飯にのせる。ソースやたれ、豚肉の銘柄に決まりはなく、家庭や店ごとに味が違う。地域によってはロースやモモも使う。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_20_gunma.html',
    icon: { label: 'ソースかつ', shape: 'rice-bowl', baseColor: '#5a3216', accentColor: '#f3ecd9' },
    reward: {
      unlockScore: 12000,
      area: '桐生市ほか（前橋市・伊勢崎市）',
      headline: '卵でとじないソースのカツ丼',
      tip: '桐生市では「カツ丼」といえば卵とじではなくソースカツ丼を指すほど親しまれている。冷めてもおいしいので、出前でよく取られていた。',
      links: searchLinks('桐生市', 'ソースカツ丼'),
      official: [{ label: '農林水産省「うちの郷土料理」ソースカツ丼', url: 'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_20_gunma.html' }],
    },
  },
  {
    id: 'himokawa-udon',
    level: 10,
    name: 'ひもかわうどん',
    shortName: 'ひもかわ',
    image: '/assets/foods/gunma/10_himokawa_udon.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '桐生市のうどんで、麺の幅がとても広く、店によっては10cm以上の幅になるものもある。つゆにつけて食べるほか、しょうゆベースのだし汁で煮込んだものもある。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_11_gunma.html',
    icon: { label: 'ひもかわ', shape: 'noodle-bowl', baseColor: '#f4efe2', accentColor: '#c99a4a' },
    reward: {
      unlockScore: 16000,
      area: '桐生市',
      headline: '幅の広い帯のような麺',
      tip: '粉食文化が根づく群馬では、うどんが主食として親しまれてきた。ひもかわは桐生市内の飲食店で味わえ、カレーうどんにした商品なども売られている。',
      links: searchLinks('桐生市', 'ひもかわうどん'),
      official: [{ label: '農林水産省「うちの郷土料理」群馬のうどん', url: 'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_11_gunma.html' }],
    },
  },
  {
    id: 'toge-no-kamameshi',
    level: 11,
    name: '峠の釜めし',
    shortName: '峠の釜めし',
    image: '/assets/foods/gunma/11_toge_no_kamameshi.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '益子焼の土釜に、鶏肉・たけのこ・しいたけ・ごぼう・栗・あんずなど9種類の山の幸を詰めた釜めし。昭和33年に発売され、安中市が市の特産品として紹介している。',
    source:
      'https://www.city.annaka.lg.jp/page/2098.html',
    icon: { label: '釜めし', shape: 'pot', baseColor: '#8a5a34', accentColor: '#e3a84a' },
    reward: {
      unlockScore: 22000,
      area: '安中市（横川）',
      headline: '益子焼の土釜に詰めた山の幸',
      tip: '碓氷線が走っていたころは、横川駅に列車が停まる4分ほどのあいだに、ホームで売られていた。',
      links: searchLinks('安中市 横川', '峠の釜めし'),
      official: [{ label: '安中市「特産品」', url: 'https://www.city.annaka.lg.jp/page/2098.html' }],
    },
  },
  {
    id: 'joshu-gyu',
    level: 12,
    name: '上州牛',
    shortName: '上州牛',
    image: '/assets/foods/gunma/12_joshu_gyu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '群馬の「上州牛」は、国内で初めてEUに輸出された牛肉とされる。群馬のすき焼きでは、上州牛に下仁田ねぎや、こんにゃく芋から作るしらたきなどを合わせ、県産の食材だけでそろえる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_19_gunma.html',
    icon: { label: '上州牛', shape: 'feast', baseColor: '#a8452f', accentColor: '#e8c9a8' },
    reward: {
      unlockScore: 30000,
      area: '群馬県全域',
      headline: '初めてEUへ渡ったとされる牛肉',
      tip: '農林水産省によると、すき焼きに入れる白菜、しゅんぎく、生しいたけは、いずれも群馬の生産量が全国5位以内。上州牛の代わりに上州軍鶏を使うすき焼きもある。',
      links: searchLinks('群馬県', '上州牛'),
      official: [{ label: '農林水産省「うちの郷土料理」すき焼き 群馬県', url: 'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/32_19_gunma.html' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const gunmaFoodStage: StageDefinition = {
  regionId: 'gunma',
  regionName: '群馬県',
  mode: 'food',
  title: '群馬めしマージ',
  theme: {
    primaryColor: '#3a6b4a',
    secondaryColor: '#ece4d2',
    accentColor: '#c0562f',
    backgroundColor: '#f9f4e8',
  },
  /** 背景ツアー「群馬めぐり」。軸は季節。5月の榛名湖から始めて、3月の楽山園で終わる。 */
  scenery: {
    tour: gunmaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, GUNMA_FOOD_HULLS),
};
